/**
 * HR API — payroll, bonuses, advances, PDF
 * @see services/hrApi.js barrel export
 */

import { api } from '../../api';

// =====================================================
// PAYROLL API FUNCTIONS
// =====================================================

/**
 * Get payroll dashboard for a given month (all employees consolidated)
 */
export const getPayrollDashboard = async (
  year,
  month,
  workContext = 'COMPANY',
  projectId = null,
  { skip = 0, limit = 25 } = {},
) => {
  try {
    const params = { year, month, work_context: workContext, skip, limit };
    if (projectId) params.project_id = projectId;
    const response = await api.get('/payroll/dashboard', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching payroll dashboard:', error);
    throw error;
  }
};

/**
 * Bulk generate payslips for multiple employees
 */
export const bulkGeneratePayslips = async (personnelIds, periodStartDate, periodEndDate) => {
  try {
    const response = await api.post('/payroll/bulk-generate', {
      personnel_ids: personnelIds,
      period_start_date: periodStartDate,
      period_end_date: periodEndDate
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk generating payslips:', error);
    throw error;
  }
};

/**
 * Calculate payroll preview (no save)
 */
export const calculatePayrollPreview = async (personnelId, periodStart, periodEnd, exchangeRate = null) => {
  try {
    const response = await api.post('/payroll/calculate-preview', {
      personnel_id: personnelId,
      period_start_date: periodStart,
      period_end_date: periodEnd,
      exchange_rate: exchangeRate
    });
    return response.data;
  } catch (error) {
    console.error('Error calculating payroll preview:', error);
    throw error;
  }
};

/**
 * Generate payslip (save to database)
 */
export const generatePayslip = async (personnelId, periodStart, periodEnd, exchangeRate = null) => {
  try {
    const response = await api.post('/payroll/generate', {
      personnel_id: personnelId,
      period_start_date: periodStart,
      period_end_date: periodEnd,
      exchange_rate: exchangeRate
    });
    return response.data;
  } catch (error) {
    console.error('Error generating payslip:', error);
    throw error;
  }
};

/**
 * Get payroll records for personnel
 */
export const getPayrollRecords = async (personnelId, filters = {}) => {
  try {
    const response = await api.get('/payroll/records', {
      params: {
        personnel_id: personnelId,
        ...filters
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching payroll records:', error);
    throw error;
  }
};

/**
 * Get single payroll record detail
 */
export const getPayrollRecordDetail = async (payrollId) => {
  try {
    const response = await api.get(`/payroll/records/${payrollId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payroll detail:', error);
    throw error;
  }
};

/**
 * Approve payslip
 */
export const approvePayslip = async (payrollId, action = 'approve', notes = '') => {
  try {
    const response = await api.post(`/payroll/records/${payrollId}/approve`, {
      action: action,
      comments: notes
    });
    return response.data;
  } catch (error) {
    console.error('Error approving payslip:', error);
    throw error;
  }
};

/**
 * Mark payslip as paid
 */
export const markPayslipPaid = async (payrollId, paymentData) => {
  try {
    const response = await api.post(`/payroll/records/${payrollId}/pay`, paymentData);
    return response.data;
  } catch (error) {
    console.error('Error marking payslip as paid:', error);
    throw error;
  }
};

/**
 * Delete draft payslip
 */
export const deletePayslip = async (payrollId) => {
  try {
    await api.delete(`/payroll/records/${payrollId}`);
    return true;
  } catch (error) {
    console.error('Error deleting payslip:', error);
    throw error;
  }
};

/**
 * Unlock timesheet for payslip
 */
export const unlockPayslipTimesheet = async (payrollId, reason) => {
  try {
    const response = await api.post(`/payroll/records/${payrollId}/unlock-timesheet`, {
      unlock_reason: reason
    });
    return response.data;
  } catch (error) {
    console.error('Error unlocking timesheet:', error);
    throw error;
  }
};

// =============================================================================
// BONUSES (Task 14.6A)
// =============================================================================

/**
 * Create a production bonus for an employee
 * 
 * @param {object} bonusData - Bonus data
 * @param {number} bonusData.personnel_id - Personnel ID
 * @param {number} bonusData.bonus_amount - Bonus amount
 * @param {string} bonusData.currency - Currency (USD, EUR, etc.)
 * @param {string} bonusData.period_start_date - Period start (YYYY-MM-DD)
 * @param {string} bonusData.period_end_date - Period end (YYYY-MM-DD)
 * @param {string} bonusData.reason - Bonus reason
 * @param {string} [bonusData.notes] - Optional notes
 * @returns {Promise} Created bonus
 */
export const createBonus = async (bonusData) => {
  try {
    const response = await api.post('/payroll/bonuses', bonusData);
    return response.data;
  } catch (error) {
    console.error('Error creating bonus:', error);
    throw error;
  }
};

/**
 * Get list of bonuses
 * 
 * @param {object} filters - Optional filters
 * @param {number} [filters.personnel_id] - Filter by personnel
 * @param {string} [filters.status] - Filter by status (PENDING, APPROVED, etc.)
 * @returns {Promise} List of bonuses
 */
export const getBonusList = async (filters = {}) => {
  try {
    const response = await api.get('/payroll/bonuses', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching bonuses:', error);
    throw error;
  }
};

/**
 * Format FastAPI/Axios error detail for display (avoids "[object Object]" in alerts).
 */
export const formatPayrollApiError = (error) => {
  const d = error?.response?.data?.detail;
  if (d == null) return error?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => (typeof x === 'string' ? x : x?.msg || JSON.stringify(x)))
      .join('; ');
  }
  if (typeof d === 'object' && d.message) return String(d.message);
  try {
    return JSON.stringify(d);
  } catch {
    return String(d);
  }
};

/**
 * Approve or reject a bonus (backend expects BonusApprovalRequest: action + comments).
 *
 * @param {number} bonusId - Bonus ID
 * @param {object} approvalData
 * @param {'approve'|'reject'} [approvalData.action='approve']
 * @param {string} [approvalData.comments] - Notes
 * @param {string} [approvalData.approval_notes] - Alias for comments (legacy callers)
 * @returns {Promise} Updated bonus
 */
export const approveBonus = async (bonusId, approvalData = {}) => {
  try {
    const action = (approvalData.action || 'approve').toLowerCase();
    const comments =
      approvalData.comments ??
      approvalData.approval_notes ??
      null;
    const response = await api.put(`/payroll/bonuses/${bonusId}/approve`, {
      action,
      comments,
    });
    return response.data;
  } catch (error) {
    console.error('Error approving bonus:', error);
    throw error;
  }
};

// =============================================================================
// CASH ADVANCES (Task 14.6B)
// =============================================================================

/**
 * Create a cash advance request
 * 
 * @param {object} advanceData - Advance data
 * @param {number} advanceData.personnel_id - Personnel ID
 * @param {number} advanceData.advance_amount - Advance amount
 * @param {string} advanceData.currency - Currency
 * @param {string} advanceData.request_date - Request date (YYYY-MM-DD)
 * @param {string} [advanceData.notes] - Optional notes
 * @returns {Promise} Created advance
 */
export const createCashAdvance = async (advanceData) => {
  try {
    const response = await api.post('/payroll/advances', advanceData);
    return response.data;
  } catch (error) {
    console.error('Error creating cash advance:', error);
    throw error;
  }
};

/**
 * Get list of cash advances
 * 
 * @param {object} filters - Optional filters
 * @param {number} [filters.personnel_id] - Filter by personnel
 * @param {string} [filters.status] - Filter by status (PENDING, APPROVED, DISBURSED, REPAID)
 * @returns {Promise} List of advances
 */
export const getAdvancesList = async (filters = {}) => {
  try {
    const response = await api.get('/payroll/advances', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching advances:', error);
    throw error;
  }
};

/**
 * Get outstanding advances (DISBURSED with remaining balance > 0)
 * 
 * @param {number} personnelId - Personnel ID
 * @returns {Promise} List of outstanding advances
 */
export const getOutstandingAdvances = async (personnelId) => {
  try {
    const response = await api.get('/payroll/advances', {
      params: {
        personnel_id: personnelId,
        status: 'DISBURSED'
      }
    });
    // Filter to only those with remaining balance > 0
    return response.data.filter(adv => parseFloat(adv.remaining_balance) > 0);
  } catch (error) {
    console.error('Error fetching outstanding advances:', error);
    throw error;
  }
};

/**
 * Approve/disburse a cash advance
 * 
 * @param {number} advanceId - Advance ID
 * @param {object} approvalData - Approval data
 * @param {string} [approvalData.action] - Action: 'approve', 'disburse', 'reject'
 * @param {string} [approvalData.approval_date] - Approval date
 * @param {string} [approvalData.disbursement_date] - Disbursement date
 * @param {string} [approvalData.approval_notes] - Optional notes
 * @returns {Promise} Updated advance
 */
export const approveAdvance = async (advanceId, approvalData = {}) => {
  try {
    // Default action to 'disburse' to approve and disburse in one step
    const data = {
      action: 'disburse',
      ...approvalData
    };
    const response = await api.put(`/payroll/advances/${advanceId}/approve`, data);
    return response.data;
  } catch (error) {
    console.error('Error approving advance:', error);
    throw error;
  }
};

/**
 * Update a cash advance
 * 
 * @param {number} advanceId - Advance ID
 * @param {object} updateData - Fields to update
 * @param {number} [updateData.advance_amount] - New amount
 * @param {string} [updateData.currency] - Currency
 * @param {string} [updateData.request_date] - Request date
 * @param {string} [updateData.status] - Status (PENDING, APPROVED, DISBURSED, REPAID, CANCELLED)
 * @param {string} [updateData.notes] - Notes
 * @param {number} [updateData.remaining_balance] - Remaining balance
 * @returns {Promise} Updated advance
 */
export const updateAdvance = async (advanceId, updateData) => {
  try {
    const response = await api.put(`/payroll/advances/${advanceId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating advance:', error);
    throw error;
  }
};

/**
 * Delete a cash advance
 * 
 * CAUTION: Only PENDING or CANCELLED advances can be deleted.
 * DISBURSED and REPAID advances cannot be deleted due to accounting implications.
 * 
 * @param {number} advanceId - Advance ID to delete
 * @returns {Promise} Deletion result
 */
export const deleteAdvance = async (advanceId) => {
  try {
    const response = await api.delete(`/payroll/advances/${advanceId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting advance:', error);
    throw error;
  }
};

// =============================================================================
// PAYROLL PDF DOWNLOAD (Task 14.5B)
// =============================================================================

/**
 * Download payslip PDF
 * 
 * @param {number} payrollId - Payroll record ID
 * @param {boolean} regenerate - Force regenerate PDF
 * @returns {Promise<Blob>} Blob response
 */
export const downloadPayslipPDF = async (payrollId, regenerate = false) => {
  try {
    const response = await api.get(`/payroll/records/${payrollId}/download-pdf`, {
      params: { regenerate },
      responseType: 'blob' // Important for binary data
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading payslip PDF:', error);
    throw error;
  }
};

/**
 * Helper: Download blob as file
 * 
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename for download
 */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Normalize blob for inline PDF viewing (correct MIME; avoids blank tab).
 */
export const toPdfBlob = (blob) => {
  if (!blob || !(blob instanceof Blob)) return blob;
  if (blob.type === 'application/pdf') return blob;
  return new Blob([blob], { type: 'application/pdf' });
};

/**
 * Open a new browsing context for PDF viewing.
 * Do not pass `noopener` in windowFeatures: HTML requires returning null when noopener is set,
 * so `window.open(..., 'noopener')` breaks {@link assignPdfBlobToWindow} even without a popup blocker.
 * We clear `opener` after open to limit reverse-tabnabbing.
 */
const openPdfBrowsingContext = (url) => {
  const w = window.open(url, '_blank');
  if (w) {
    try {
      w.opener = null;
    } catch {
      /* ignore */
    }
  }
  return w;
};

/**
 * Open a PDF blob in a new tab. Popup blockers often block window.open() after await,
 * so call {@link openBlankPdfWindow} synchronously in the click handler first, then
 * {@link assignPdfBlobToWindow} when the download finishes.
 */
export const openBlobInNewTab = (blob) => {
  const pdfBlob = toPdfBlob(blob);
  const url = window.URL.createObjectURL(pdfBlob);
  const w = openPdfBrowsingContext(url);
  if (w) {
    setTimeout(() => window.URL.revokeObjectURL(url), 120000);
  } else {
    window.URL.revokeObjectURL(url);
  }
  return w;
};

/** Call synchronously from onClick before any await (avoids popup blockers). */
export const openBlankPdfWindow = () => openPdfBrowsingContext('about:blank');

/**
 * Navigate a window (from openBlankPdfWindow) to a PDF blob URL.
 */
export const assignPdfBlobToWindow = (win, blob) => {
  if (!win) return false;
  const pdfBlob = toPdfBlob(blob);
  const url = window.URL.createObjectURL(pdfBlob);
  win.location.href = url;
  setTimeout(() => window.URL.revokeObjectURL(url), 120000);
  return true;
};

/**
 * Check if payslip can be generated for a period
 *
 * @param {number} personnelId - Personnel ID
 * @param {string} periodStartDate - Period start (YYYY-MM-DD)
 * @param {string} periodEndDate - Period end (YYYY-MM-DD)
 * @returns {Promise<Object>} Validation response with can_generate, reason, status
 */
export const canGeneratePayslip = async (personnelId, periodStartDate, periodEndDate) => {
  try {
    const response = await api.get('/payroll/can-generate', {
      params: {
        personnel_id: personnelId,
        period_start_date: periodStartDate,
        period_end_date: periodEndDate,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error checking payslip eligibility:', error);
    throw error;
  }
};


